import { FC, useEffect, useState } from 'react';
import { Notification, type NotificationTheme, Theme, useTranslation } from '@nextgen-web-framework/all';
import { StartKitDetailType, CartAccountProcess } from '@commerce-profile-webcomponents/sdk';
import { useCartConfig } from '../../utils/masterdata';
import { parseString } from '../../utils/helper';
import ProductSelectionDrawer from '../Common/ProductSelectionDrawer/ProductSelectionDrawer';
import { AccountLifeCycleKitType, AccountTypeCode } from '../../utils/Constants';
import { RegistrationSelectStarterKitProps } from './types/propTypes';

const RegistrationSelectStarterKit: FC<RegistrationSelectStarterKitProps> = ({
  openSKUSelector,
  accountType,
  onConfirm,
}) => {
  const t = useTranslation();

  const cartConfigData = useCartConfig(); // get cart config by country
  const [cartConfig] = cartConfigData;
  const regConfig = cartConfig?.accountProcess?.registration;
  const showEkitTermsCondition = Boolean(cartConfig?.accountProcess?.showEkitTermsCondition);
  const memberType = AccountTypeCode[accountType] as keyof CartAccountProcess;
  const skuConfig = regConfig?.[memberType as keyof CartAccountProcess];

  const skuDefaultKey = parseString(regConfig?.[memberType]?.default, AccountLifeCycleKitType.E_KIT);
  const defaultSku = parseString(skuConfig?.[skuDefaultKey]);
  const feeSku = parseString(skuConfig?.fee);

  const [starterKits, setStarterKits] = useState<StartKitDetailType[]>([]);
  const [showDrawer, setShowDrawer] = useState(false);

  useEffect(() => {
    const kitList: StartKitDetailType[] = [];

    if (skuConfig?.eKit) {
      kitList.push({
        sku: parseString(skuConfig?.eKit),
        tncEnabled: showEkitTermsCondition,
        productName: t('profile.AccountRenewal.selectKit.eKit'),
        productDescription: t('profile.AccountRenewal.selectKit.selected.eKit.desc'),
        selectedTitle: t('profile.SelectStarterKit.selectKit.eKit.productName'),
      });
    }
    if (skuConfig?.pKit) {
      kitList.push({
        sku: parseString(skuConfig?.pKit),
        tncEnabled: false,
        productName: t('profile.AccountRenewal.selectKit.pKit'),
        productDescription: t('profile.AccountRenewal.selectKit.selected.pKit.desc'),
        selectedTitle: t('profile.SelectStarterKit.selectKit.pKit.productName'),
      });
    }
    setStarterKits(kitList);
  }, [skuConfig]);

  useEffect(() => {
    if (starterKits.length === 1) {
      setShowDrawer(false);

      const selectedKit = starterKits.find((kit) => kit.sku === defaultSku);
      if (openSKUSelector && selectedKit) {
        onConfirm(selectedKit.sku, feeSku);
      }
    } else {
      setShowDrawer(true);
    }
  }, [openSKUSelector, starterKits]);

  const handleKitSelection = (kit: string) => {
    onConfirm(kit, feeSku);
  };

  const handleClose = () => {
    setShowDrawer(false);
    onConfirm('', '', true);
  };

  if (openSKUSelector && showDrawer) {
    return (
      <ProductSelectionDrawer
        title={t('profile.Registration.selectKit.title')}
        productTitle={t(`profile.registration.selectKitTitle.${memberType}`)}
        canClose={true}
        onProductSelect={handleKitSelection}
        onClose={handleClose}
        products={starterKits}
        selectedSku={defaultSku}
        showCancelButton={false}
        alertMessage={
          <Notification
            theme={Theme.WARNING as NotificationTheme}
            message={t(`profile.registration.kitTypeInstruction`)}
            hasBoldText={false}
            zIndex={1}
          />
        }
      />
    );
  } else {
    return <></>;
  }
};

export default RegistrationSelectStarterKit;
